import { useEffect, useRef, useState } from "react";
import { AlertCircle, Search } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Select } from "semantic-ui-react";
import {
  addAlertData,
  searchAlerts,
  updateAlertData,
} from "../../api/alertRequestApi";
import { getBulkDropdownData, getDropdownData } from "../../api/keyvalue";
import AlertAddComponent from "../../components/alert/AlertAddComponent";
import Grid from "../../components/Grid";
import Button from "../../components/input/Button";
import Dropdown from "../../components/input/Dropdown";
import SideDrawer from "../../components/input/SideDrawer";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  ActiveCellStyle,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import Tab from "../../components/Tab";
import { FORMS, MODULES } from "../../const";
import { errorMessageToast } from "../../helper";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useUserCompany from "../../hooks/useUserCompany";
import { errorActions } from "../../store/error-slice";

const FORM = FORMS.ALERTS.ALR.form;

const Alert = () => {
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Admin,
    section: FORMS.ALERTS,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const dispatch = useDispatch();
  const errorState = useSelector((state) => state.error.alertDefaultErrors);
  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);
  const [formData, setFormData] = useState({
    nodCode: null,
    nodStaCodeId: null,
    nodStaCode: null,
    nodDefault: null,
    alertType: null,
    status: null,
    active: null,
    fromDate: null,
    toDate: null,
  });

  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [editAlerts, setEditAlerts] = useState({});
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // dropdown
  const [currentErrors, setCurrentErrors] = useState({});
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [alertTypeOpt, setAlertTypeOpt] = useState([]);
  const [staCodeOpt, setStaCodeOpt] = useState([]);
  const [defaultTypeOpt, setDefaultTypeOpt] = useState([]);
  const [alertStatusOpt, setAlertStatusOpt] = useState([]);
  const [alertActiveOpt, setAlertActiveOpt] = useState([]);
  const [statusCodeOpt, setStatusCodeOpt] = useState([]);
  const alertRef = useRef();
  const [staCodeMap, setStaCodeMap] = useState({});

  const alertCodeRef = useRef("");
  const alertNodStaCodeRef = useRef("");
  const alertNodDefaultId = useRef("");
  const alertNodAlertType = useRef("");
  const [toDate, setToDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const userCompany = useUserCompany();
  const user = useSelector((state) => state.user);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const staOpt = await getDropdownData("evn_type", "status", [
        "keyValue2",
        "keyValue3",
      ]);
      setStatusCodeOpt(staOpt);

      setStaCodeMap(
        staOpt.reduce((acc, cur) => {
          acc[cur.key] = cur.keyValue2;
          return acc;
        }, {})
      );

      const {
        active = [],
        status = [],
        alert_type: alertTypeOpt = [],
        default_select_types: defaultTypeOpt = [],
      } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
        { key: "default_select_types" },
        { key: "alert_type" },
      ]);

      setActiveOptions(active);
      setStatusOptions(status);
      setAlertTypeOpt(alertTypeOpt);
      setDefaultTypeOpt(defaultTypeOpt);
      setAlertStatusOpt(status);
      setAlertActiveOpt(active);

      setIsLoading(false);
    })();
  }, []);

  useEffect(() => {
    setFormData((prevState) => ({
      ...prevState,
      nodStaCode: staCodeMap[prevState.nodStaCodeId],
    }));
  }, [formData.nodStaCodeId]);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditAlerts(null);
    setDrawerTab(1);
  };
  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const reset = () => {
    alertRef.current.resetForm();

    setEditMode(false);
    setEditAlerts(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);
    setIsSubmitLoading(true);
    dispatch(errorActions.clearAlertError());

    const alertErrors = alertRef.current.validateForm();

    if (Object.keys(alertErrors).length > 0) {
      dispatch(
        errorActions.validateAlerts({
          alertRequest: alertRef.current.getData(),
        })
      );
      setIsSubmitLoading(false);
      toast.error("There was an error adding Alerts", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }

    try {
      const alertPayload = {
        nodReference: alertRef.current.getData().nodReference,
        nodStaCode: alertRef.current.getData().nodStaCode,
        nodDefaultId: alertRef.current.getData().nodDefaultId,
        nodDescription: alertRef.current.getData().nodDescription,
        nodAlertType: alertRef.current.getData().nodAlertType,
        nodStatus: alertRef.current.getData().nodStatus,
        nodActive: alertRef.current.getData().nodActive,
        createdBy: alertRef.current.getData().createdBy,
        createdDate: alertRef.current.getData().createdDate,
        lastModifiedBy: alertRef.current.getData().lastModifiedBy,
        lastModifiedDate: alertRef.current.getData().lastModifiedDate,
        nodCompany: userCompany.comId,
        author: user.username,
      };

      if (editMode) {
        await updateAlertData({
          ...alertPayload,
          nodId: alertRef.current.getData().nodId,
        });
        toast.success("Alert updated successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
      } else {
        await addAlertData({
          ...alertPayload,
          nodCode: "",
        });
        toast.success("Alert added successfully!", {
          autoClose: 5000,
          containerId: "Main",
        });
      }

      handleDrawerClose();
      reset();
    } catch (e) {
      errorMessageToast(e);
    }

    setIsSubmitLoading(false);
  };

  const handleSearchSubmit = async (e) => {
    e.preventDefault();

    if (tab === 1) {
      setIsLoading(false);
      setActiveAccordion1(false);
      setResetVisible(true);
      setSearchClicked(true);
      setSearchSubmitLoading(true);
    }
    const normalizeValue = (val) => (val === "" ? null : val);

    const response = await searchAlerts({
      nodCode: normalizeValue(alertCodeRef.current.value?.trim()),
      nodStaCode: normalizeValue(alertNodStaCodeRef.current.value),
      nodDefaultId: normalizeValue(alertNodDefaultId.current.value),
      nodAlertType: normalizeValue(alertNodAlertType.current.value),
      toDate: normalizeValue(toDate),
      fromDate: normalizeValue(fromDate),
      nodStatus: normalizeValue(status),
      nodActive: normalizeValue(active),
    });

    setSearchRes(response);
    setSearchSubmitLoading(false);
  };

  const handleSearchReset = (e) => {
    e.preventDefault();
    alertCodeRef.current.value = "";
    alertNodStaCodeRef.current.value = "";
    alertNodDefaultId.current.value = null;
    alertNodAlertType.current.value = null;
    setStatus("");
    setActive("");
    setFromDate("");
    setToDate("");
    setFormData({
      nodCode: null,
      nodStaCode: null,
      nodStaCodeId: null,
      nodDefault: null,
      alertType: null,
      status: null,
      active: null,
      fromDate: null,
      toDate: null,
    });
  };

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div className="ui tabular menu attached">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <AlertCircle size={15} style={{ marginRight: "5px" }} />
            </span>
            Alert Search
          </div>
        </div>
        <Button
          template="drawerOpen"
          onClick={handleDrawerOpen}
          authenticated={true}
          form={FORMS.ALERTS.ALR.form}
          module={MODULES.Admin}
          // disabled={firstViableTab === -1}
          editMode={false}
        />
      </div>
      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {}}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isSearchLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form">
                <h4 className="ui diving header">Alert Information</h4>
                <div className="three fields">
                  <div className="field">
                    <label>Alert Code</label>
                    <input
                      type="text"
                      placeholder="Alert Code"
                      name="nodCode"
                      ref={alertCodeRef}
                    />
                  </div>
                  <Dropdown
                    label="Notification Status Code"
                    error={currentErrors.nodStaCode}
                    module={MODULES.Admin}
                    form={FORM}
                    field="nod_sta_code"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { nodStaCode: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    clearable
                    placeholder="Notification Status Code"
                    style={{ height: "5px" }}
                    ref={alertNodStaCodeRef}
                    options={statusCodeOpt}
                    value={formData.nodStaCodeId}
                    onChange={(e, data) => {
                      setFormData({
                        ...formData,
                        nodStaCodeId: data.value,
                      });
                    }}
                  />
                  <Dropdown
                    label="Default Selected Type"
                    error={currentErrors.nodDefault}
                    module={MODULES.Admin}
                    form={FORM}
                    field="nod_default_id"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { nodDefault: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    clearable
                    placeholder="Default Selected Type"
                    style={{ height: "5px" }}
                    ref={alertNodDefaultId}
                    options={defaultTypeOpt}
                    value={formData.nodDefault}
                    onChange={(e, data) => {
                      setFormData({
                        ...formData,
                        nodDefault: data.value,
                      });
                    }}
                  />

                  <Dropdown
                    label="Alert Type"
                    error={currentErrors.alertType}
                    module={MODULES.Admin}
                    form={FORM}
                    field="nod_alert_type"
                    editMode={editMode}
                    resetError={() =>
                      setCurrentErrors((prevState) => {
                        const { alertType: _, ...rest } = prevState;
                        return rest;
                      })
                    }
                    clearable
                    placeholder="Alert Type"
                    style={{ height: "5px" }}
                    ref={alertNodAlertType}
                    options={alertTypeOpt}
                    value={formData.alertType}
                    onChange={(e, data) => {
                      setFormData({
                        ...formData,
                        alertType: data.value,
                      });
                    }}
                  />
                </div>
                <div className="four fields">
                  <div className="field">
                    <label>From Date</label>
                    <input
                      type="date"
                      name="alertToDate"
                      placeholder="To Date"
                      value={fromDate}
                      onChange={(e) => {
                        setFromDate(e.target.value);
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input
                      type="date"
                      name="alertFromDate"
                      placeholder="To Date"
                      value={toDate}
                      onChange={(e) => {
                        setToDate(e.target.value);
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>Active</label>
                    <Select
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div className="field" style={{ width: "100%" }}>
                    <Button
                      template="search"
                      onClick={(e) => {
                        handleSearchSubmit(e);
                      }}
                    />
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                config={[
                  {
                    headerName: "Id",
                    field: "nod_id",
                    sort: "desc",
                    hide: true,
                  },
                  {
                    headerName: "Alert Code",
                    field: "nodCode",
                  },
                  {
                    headerName: "Notification Status Code",
                    field: "nodStaCodeLabel",
                  },
                  {
                    headerName: "Selected Type",
                    field: "nodDefaultLabel",
                  },
                  {
                    headerName: "Alert Type",
                    field: "alertTypeLabel",
                  },
                  {
                    headerName: "Status",
                    field: "status",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "active",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  setEditAlerts(props.data);
                  setEditMode(true);
                  setIsDrawerOpen(true);
                  setSearchFetchLoading(false);
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <>
              <div
                className="alert-code-header"
                style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
              >
                <h3>Alert Code :{editAlerts?.nodCode}</h3>
              </div>
            </>
          ) : null
        }
        body={
          <form className="drawer-content" onSubmit={handleSubmit}>
            <div style={{ width: "60vw", boxShadow: "none" }}>
              <div className="ui top attached tabular menu">
                <Tab
                  Icon={AlertCircle}
                  onClick={() => {
                    setDrawerTab(1);
                  }}
                  active={drawerTab === 1}
                  errors={
                    Object.keys(errorState.alertDefaultErrors).length !== 0
                  }
                  label="Alerts"
                  authenticated={true}
                  form={FORMS.ALERTS.ALR_DETAIL.form}
                  showMandatory={true}
                  isMandatory={true}
                />
              </div>
              <div
                className="ui bottom attached active tab segment"
                style={{ display: drawerTab === 1 ? "block" : "none" }}
              >
                <AlertAddComponent
                  ref={alertRef}
                  isLoading={isLoading}
                  errors={errorState.alertDefaultErrors}
                  dropdown={{
                    alertTypeOpt,
                    statusCodeOpt,
                    defaultTypeOpt,
                    alertStatusOpt,
                    alertActiveOpt,
                    staCodeMap,
                  }}
                  editMode={editMode}
                  currentValues={editMode ? editAlerts : null}
                />
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {editMode ? (
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORMS.ALERTS.ALR.form}
                      module={MODULES.Admin}
                      section={FORMS.ALERTS}
                      authenticated={true}
                      editMode={editMode}
                    />
                  ) : drawerTab === 1 && !isLoading ? (
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORMS.ALERTS.ALR.form}
                      module={MODULES.Admin}
                      section={FORMS.ALERTS}
                      authenticated={true}
                      editMode={editMode}
                    />
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>
          </form>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default Alert;
